import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { PLUGIN_VERSIONS, PluginInitializerService } from 'core-client/api';
import { APP_VERSION } from '../../environments/version';

// version/buildDate are build-stamped into version.ts (env-injected; nearest git tag locally).
// The plugin table is hand-rendered (not console.table) so the first column reads "plugin"
// rather than console.table's hardcoded "(index)".
export function provideVersionLog(): EnvironmentProviders {
  return provideAppInitializer(async () => {
    const initSvc = inject(PluginInitializerService);

    // The plugin initializers queue their registrations in this same tick; wait for the queue.
    await Promise.resolve();
    await initSvc.whenRegistrationDone();
    const unavailable = new Set(initSvc.getSkippedPlugins().map(normalizePluginName));

    const entries = Object.entries(PLUGIN_VERSIONS).map(([name, version]) => ({
      name,
      version,
      status: unavailable.has(normalizePluginName(name)) ? 'unavailable' : 'ready',
    }));
    const nameW = Math.max('plugin'.length, ...entries.map((e) => e.name.length));
    const verW = Math.max('version'.length, ...entries.map((e) => e.version.length));
    const statusW = Math.max('status'.length, ...entries.map((e) => e.status.length));
    const sep = (l: string, m: string, r: string): string =>
      `${l}${'─'.repeat(nameW + 2)}${m}${'─'.repeat(verW + 2)}${m}${'─'.repeat(statusW + 2)}${r}`;
    const row = (a: string, b: string, c: string): string =>
      `│ ${a.padEnd(nameW)} │ ${b.padEnd(verW)} │ ${c.padEnd(statusW)} │`;
    const table = [
      sep('┌', '┬', '┐'),
      row('plugin', 'version', 'status'),
      sep('├', '┼', '┤'),
      ...entries.map((e) => row(e.name, e.version, e.status)),
      sep('└', '┴', '┘'),
    ].join('\n');
    // One message, so the heading and the table can never be shown or dropped separately.
    console.log(
      `%cADDboard%c v${APP_VERSION.version}%c · built ${APP_VERSION.buildDate.slice(0, 10)}\n%c${table}`,
      'font-weight:bold;color:#fff;background:#2196F3;padding:2px 6px;border-radius:3px',
      'font-weight:bold;color:#2196F3;font-size:1.15em',
      'color:#888',
      'font-family:monospace;color:inherit;font-size:inherit;font-weight:normal',
    );
  });
}

// Queue names ("wmsChartLoader") and folder names ("wms-chart-loader") only agree without case and separators.
function normalizePluginName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}
